/**
 * Summary screen.
 *
 * Shown when:
 *   - Runner answers the final item and calls completeSession()
 *     + api.navigateTo('summary', {}), OR
 *   - main.js boot detects session.completed === true at app open.
 *
 * Responsibilities:
 *   1. Render header "[listName] — N packed, M missing" driven by the
 *      session's items + results arrays.
 *   2. Render each item with summary-item--yes / summary-item--no
 *      coloring and a check / X icon.
 *   3. Tap any item to toggle yes <-> no via session.setResultAtIndex,
 *      flushing synchronously and surfacing write-fail toast on error.
 *   4. Restart: clear session, start a fresh one from the CURRENT list
 *      (fetched by getListById at click-time so renames apply),
 *      navigate to Runner.
 *   5. Done: clear session, navigate to Home.
 *   6. Defensive guards: no session -> home; incomplete session -> runner.
 *
 * The Summary is the sole writer of completed-session results. It never
 * calls safeWrite directly; it uses session.setResultAtIndex.
 */

// We import the session module as a namespace (and re-export its callable
// functions through local `const` bindings) for the same reason modal-stubs.js
// uses `import * as / export const`: summary.test.js relies on
// `vi.spyOn(session, 'setResultAtIndex').mockReturnValue(...)` to intercept
// the write-failure branch. Named imports bind to the exporter's *live
// binding* at module load and bypass later spy installation; calling through
// the namespace object (`sessionNs.setResultAtIndex(...)`) routes through
// the property the test has overridden.
import * as sessionNs from '../session.js';
import { getListById } from '../lists.js';

const getSession = () => sessionNs.getSession();
const clearSession = () => sessionNs.clearSession();
const startSession = (list) => sessionNs.startSession(list);
const setResultAtIndex = (idx, val) => sessionNs.setResultAtIndex(idx, val);

const WRITE_FAIL_MSG = "Couldn't save — progress may not survive close";

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function countResults(results) {
  let packed = 0, missing = 0;
  for (const r of results) {
    if (r === 'yes') packed += 1;
    else if (r === 'no') missing += 1;
  }
  return { packed, missing };
}

/**
 * @param {HTMLElement} root
 * @param {object} _params
 * @param {{navigateTo: Function, showToast: Function, showError: Function, announce: Function}} api
 */
export function renderSummary(root, _params, api) {
  const sessionState = getSession();

  // Defensive guards (match Runner's pattern).
  if (!sessionState) {
    api.navigateTo('home', {});
    return;
  }
  if (!sessionState.completed) {
    api.navigateTo('runner', {});
    return;
  }

  doRender();

  function doRender() {
    const s = getSession();
    const { packed, missing } = countResults(s.results);

    root.innerHTML = `
      <main class="screen screen-summary" aria-labelledby="summary-heading">
        <header class="summary-header">
          <h1 id="summary-heading" tabindex="-1">${escapeHtml(s.listName)}</h1>
          <p class="summary-stats" aria-live="polite">
            ${packed} packed, ${missing} missing
          </p>
        </header>

        <ul class="summary-list" role="list">
          ${s.items.map((itemText, i) => {
            const state = s.results[i];
            const cls = state === 'yes' ? 'summary-item--yes' : 'summary-item--no';
            const icon = state === 'yes' ? '\u{1F48B}' : '\u{1F577}\u{FE0F}';
            const label = state === 'yes' ? 'Packed' : 'Missing';
            return `
              <li role="listitem">
                <button type="button" class="summary-item ${cls}"
                        data-index="${i}"
                        aria-label="${label}: ${escapeHtml(itemText)}. Tap to toggle.">
                  <span class="summary-item__icon" aria-hidden="true">${icon}</span>
                  <span class="summary-item__text">${escapeHtml(itemText)}</span>
                </button>
              </li>
            `;
          }).join('')}
        </ul>

        <footer class="summary-actions">
          <button type="button" class="summary-btn summary-btn--restart"
                  data-action="restart">Restart</button>
          <button type="button" class="summary-btn summary-btn--done"
                  data-action="done">Done</button>
        </footer>
      </main>
    `;

    // Announce once per render so VoiceOver surfaces the totals.
    api.announce(`${s.listName} complete. ${packed} packed, ${missing} missing.`);

    // Item tap handlers.
    root.querySelectorAll('.summary-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.getAttribute('data-index'));
        const current = getSession().results[idx];
        const next = current === 'yes' ? 'no' : 'yes';
        const { writeOk } = setResultAtIndex(idx, next);
        if (!writeOk) {
          api.showError(WRITE_FAIL_MSG);
          // Still re-render so the UI reflects in-memory state.
        }
        doRender();
      });
    });

    // Restart.
    root.querySelector('[data-action="restart"]').addEventListener('click', () => {
      const current = getListById(s.listId);
      if (!current) {
        api.showError('That list no longer exists.');
        return;
      }
      clearSession();
      // startSession returns { session, writeOk } per Batch 2 contract.
      // Surface the write-failure toast on storage-full so the user knows
      // the restarted session may not survive close -- consistent with
      // setResultAtIndex's error handling above.
      const { writeOk } = startSession(current);
      if (!writeOk) {
        api.showError(WRITE_FAIL_MSG);
      }
      api.navigateTo('runner', {});
    });

    // Done.
    root.querySelector('[data-action="done"]').addEventListener('click', () => {
      clearSession();
      api.navigateTo('home', {});
    });
  }
}
